import { drain } from './outbox'
import { cancelSyncNudge } from './sync-nudge'
import { registerAllOps } from './register'
import { revalidateStale } from './pull/pull-engine'
import { warmOrgConfigs } from './pull/org-config'
import { purgeUploaded } from '../photos/purge'
import { sweepOrphanPhotos } from '../photos/photoIssues'
import { pruneOutboxThrottled } from './prune'
import { isFlagEnabled } from 'src/offline/flags'
import { isReallyOnline } from 'src/services/connectivity'
import { dbgWarn, dbgError } from '../logger'

/**
 * sync-worker.js — dispara el drain del outbox (re-impl LIMPIA de v1 sync-worker.js).
 *
 * Background Sync API NO existe en iOS (ni WKWebView) -> la sincronizacion SOLO ocurre con la app
 * abierta. Disparadores: evento `online`, `focus`, `visibilitychange` (al volver visible) y un
 * timer periodico. Cada tick valida la red real (no solo navigator.onLine) para evitar portales
 * cautivos. `offline_mode_enabled` solo frena el PULL de catálogos (los usa Tareas); el drenado de lo ya
 * capturado nunca depende de la bandera. `isReallyOnline` viene de src/services/connectivity.
 *
 * recoverInFlight NO se llama aqui: se llama en el BOOT, ANTES de startSyncWorker (decision F2
 * [03-02]: F2 EXPONE recoverInFlight; F3 la cablea en el boot antes del worker). Ver boot/offline-v2.js.
 */

const TICK_INTERVAL = 30000
let timer = null
let started = false
// Fix 1 (04-02, cross-plan additive): guard idempotente para registrar los handlers del push UNA
// sola vez en el camino de produccion del drain. Sin esto, boot->startSyncWorker->drain->dispatch
// lanzaria "Sin handler para op" en el primer push real (el registry shippea vacio en 04-01).
let _registered = false

// SINGLE-FLIGHT (hardening e2e 2026-06-10): si un tick sigue corriendo (backend lento — un
// pull de catalogos puede tardar minutos), los ticks del timer/online/visibility se SALTAN
// en vez de encimarse. Sin esto, cada tick de 30s re-jalaba los MISMOS catalogos en paralelo
// (el cursor aun no se escribia) y la estampida saturo el backend local (visto en vivo: pool
// FPM agotado). El drain ya es idempotente por claim, pero el pull NO debe solaparse.
let _ticking = false

/**
 * tick — envuelve el ciclo real con LEADER ELECTION entre pestañas (Web Locks). En web multi-tab, cada
 * pestaña arranca su propio worker y sus guards (draining/_ticking/authPaused) son POR PESTAÑA: con el
 * fallback wa-sqlite-idb (IndexedDB, sin el lock exclusivo del pool de OPFS) dos pestañas sincronizarían
 * en PARALELO -> doble push + BD divergente. `navigator.locks` con ifAvailable deja que solo UNA pestaña
 * corra el ciclo a la vez (las demás saltan). En nativo (una sola WebView) o navegadores sin la API, se
 * corre directo (comportamiento previo). [review Hugo]
 */
async function tick (opts) {
  if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
    return navigator.locks.request('offline-v2-sync-tick', { ifAvailable: true }, async (lock) => {
      if (!lock) return // otra pestaña ya está en su tick -> saltar (no doble-sync)
      await runTick(opts)
    })
  }
  return runTick(opts)
}

// opts.force: nudge EXPLÍCITO del usuario (cierre finalizado / botón "sincronizar ahora") — salta SOLO
// el gate de red. Los listeners (online/focus/visibilitychange) llaman tick(Event) y el timer tick() sin
// args -> destructurar { force = false } de un Event/undefined da false: sin cambio para los automáticos.
async function runTick ({ force = false } = {}) {
  // NUDGE FORZADO: NO gatear el drain tras el probe frágil. `navigator.onLine` se queda pegado en false
  // en iOS/WKWebView aun con backend alcanzable -> isReallyOnline() cortaba en cada tick y el cierre
  // encolado quedaba pending hasta el próximo evento 'online' (repro: 140s+). drain() es seguro sin red
  // (guards authPaused/draining + claim idempotente + backoff en el HTTP). El single-flight del pull
  // (_ticking) sigue intacto; sólo los nudges explícitos fuerzan.
  if (!force && !(await isReallyOnline())) return
  // HALLAZGO e2e 2026-06-10: con timeouts realistas un pull de catalogos puede tardar MINUTOS;
  // si el single-flight cubre TODO el tick, los syncNow siguientes se saltan y el PUSH de
  // capturas nuevas (y la purga) quedan hambreados detras del pull largo. El drain tiene su
  // propio guard de reentrancia ('draining') y el write-mutex serializa el SQL, asi que es
  // seguro drenar/purgar en CADA tick; el single-flight protege SOLO la seccion de pull.
  try { await drain() } catch (e) { dbgError('sync', 'drain error', e) }
  try { await purgeUploaded() } catch (e) { dbgWarn('sync', 'purge skipped', e) }
  // Detección visible de fotos huérfanas (capturadas pero sin op: crash/write fallido) -> sync_notifications.
  try { await sweepOrphanPhotos() } catch (e) { dbgWarn('sync', 'orphan-photo sweep skipped', e) }
  // PODA (deuda de tuning): borra filas done viejas del outbox DESPUES de purgar fotos (la purga
  // necesita el done del parent; prune.js ademas trae su propio guard de photo_meta). Throttled a
  // cada 6h dentro — en los demas ticks es un no-op.
  try { await pruneOutboxThrottled() } catch (e) { dbgWarn('sync', 'prune skipped', e) }
  if (!isFlagEnabled('offline_mode_enabled')) return
  if (_ticking) return
  _ticking = true
  try {
    // CROSS-PLAN ADITIVO (05-01, F4): PUSH primero, luego PULL (05-RESEARCH.md Pattern 3).
    // Drenar el outbox antes de revalidar significa que el pull ve estado de servidor que ya
    // refleja lo recien empujado, minimizando conflictos espurios del LWW-guard. El pull es
    // BEST-EFFORT: su propio try/catch garantiza que NUNCA bloquea ni falla el drain. SCOPE
    // (I6): no se pasa scope explicito; revalidateStale -> getScope(undefined) resuelve org/user
    // de la sesion internamente, y cualquier descriptor que aun no resuelva scope salta limpio
    // ({skipped:'no-scope'}, logueado) — no hay path revalidateStale(undefined)->scope.organizationId
    // que lance TypeError. NO se corre drain y pull en paralelo.
    // `force` viene del nudge EXPLICITO del usuario (boton de sincronizar, cierre): en ese caso el
    // pull ignora la ventana de backoff. Es la unica salida que tiene un catalogo atascado en el
    // escalon de 6 h, porque en produccion no hay ningun otro caller de los catalogos (TKT-2240).
    try { await revalidateStale(undefined, { force }) } catch (e) { dbgWarn('sync', 'pull revalidate skipped', e) }
    // DOWN-SYNC de trabajo asignado (tasks del operador + occurrences propias): ELIMINADO
    // (2026-08-14). El consumidor se shippeó sin el productor: GET /api/tasks lo servía el index
    // genérico de CRUDController (ignoraba filtros, COUNT + deep OFFSET sobre toda la org) hasta
    // saturar la flota el 22-jul, cuando el backend eliminó la ruta (graphql-api d6341331);
    // GET /api/occurrences (colección) jamás existió. Sin ruta, el pull martillaba 405/404 cada 30s
    // por cliente sin backoff y volvió a saturar la flota el 14-ago (20.6% del tiempo de origen).
    // Si ese down-sync se retoma: el backend debe exponer ANTES ambos endpoints scoped con ?since=
    // + server_version, y la re-implementación debe traer guard de sesión (sin usuario no hay pull),
    // circuit breaker para 4xx permanente (un endpoint que no existe no se arregla reintentando
    // cada 30s) y feature flag propio apagado por default.
    // WARM-UP org-config (ap_categories/ap_categories2 -> meta): catálogos del diálogo de tarea
    // que no son descriptor Tier B. TTL propio dentro (12h); mismo carril best-effort del pull.
    try { await warmOrgConfigs() } catch (e) { dbgWarn('sync', 'org-config warm skipped', e) }
    // CROSS-PLAN ADITIVO (06-02, F5): PURGA de binarios de foto tras subida confirmada (MOD-05).
    // Orden DRAIN -> PULL -> PURGE. purgeUploaded() es un sweep idempotente que SOLO borra el
    // binario de una foto cuyo padre ya NO tiene op pending/in_flight Y tiene un op 'done'
    // (subida confirmada), volteando photo_meta.upload_status='done'. NO va en commitSuccess (un
    // borrado de FS no es transaccional con SQLite). BEST-EFFORT: su propio try/catch garantiza
    // que NUNCA bloquea ni falla el tick (espeja revalidateStale). CROSS-PLAN: 06-01 (Wave 2)
    // DEBE re-leer este archivo y PRESERVAR este hook de purga.
  } catch (error) {
    dbgError('sync', 'sync-worker tick error', error)
  } finally {
    _ticking = false
  }
}

function onVisible () {
  if (document.visibilityState === 'visible') tick()
}

/** Arranca el worker (idempotente). */
export function startSyncWorker () {
  // Registra los handlers del push ANTES de que cualquier drain pueda disparar (idempotente por
  // _registered).
  if (!_registered) { registerAllOps(); _registered = true }
  if (started) return
  started = true
  window.addEventListener('online', tick)
  window.addEventListener('focus', tick)
  document.addEventListener('visibilitychange', onVisible)
  timer = setInterval(tick, TICK_INTERVAL)
}

/** ¿El worker ya arrancó en esta sesión? (boot, o login después de un boot que no abrió la base) */
export function isSyncWorkerStarted () {
  return started
}

/** Detiene el worker (logout/reset). */
export function stopSyncWorker () {
  // El nudge coalescido vive en estado de modulo y su timer no muere con el intervalo del worker
  // (TKT-2240): sin esto podia disparar un drain hasta 30 s despues del logout, con los tokens ya
  // borrados -> 401 y authPaused colgado hasta el siguiente login.
  cancelSyncNudge()
  if (timer) clearInterval(timer)
  timer = null
  window.removeEventListener('online', tick)
  window.removeEventListener('focus', tick)
  document.removeEventListener('visibilitychange', onVisible)
  started = false
}

/**
 * Fuerza un ciclo de sync inmediato (ej. boton "sincronizar ahora" o el nudge post-finalizar).
 * `{ force: true }` salta el gate de red (isReallyOnline) para que un cierre encolado drene de una,
 * sin depender de navigator.onLine. Sin args -> force=false (backward-compat con los callers existentes).
 */
export function syncNow ({ force = false } = {}) {
  return tick({ force })
}
